import { useTranslation } from 'react-i18next';
import useScrollReveal from '@/hooks/useScrollReveal';

const heroProducts = [
  {
    seq: 'kremsi-hero-natural',
    query: 'Elegant%20Greek%20yogurt%20cup%20with%20natural%20yogurt%2C%20minimalist%20white%20ceramic%20bowl%2C%20soft%20warm%20lighting%2C%20cream%20colored%20packaging%20with%20subtle%20Greek%20motif%2C%20clean%20product%20photography%20on%20white%20background%2C%20professional%20food%20styling%2C%20soft%20shadows%2C%20editorial%20quality%2C%20high%20end%20aesthetic',
    label: 'Natural',
  },
  {
    seq: 'kremsi-hero-vainilla',
    query: 'Premium%20vanilla%20Greek%20yogurt%20product%20packaging%2C%20elegant%20cream%20white%20cup%20design%2C%20soft%20pastel%20pink%20accents%2C%20minimalist%20branding%2C%20clean%20product%20photography%20on%20white%20background%2C%20professional%20food%20photography%2C%20soft%20natural%20lighting%2C%20delicate%20shadows%2C%20premium%20packaging%20design',
    label: 'Vainilla',
  },
  {
    seq: 'kremsi-hero-taro',
    query: 'Taro%20flavored%20Greek%20yogurt%20elegant%20packaging%2C%20soft%20purple%20cream%20tones%2C%20minimalist%20white%20cup%20design%2C%20premium%20product%20photography%20on%20white%20background%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20professional%20food%20styling%2C%20editorial%20quality%2C%20delicate%20shadows',
    label: 'Taro',
  },
  {
    seq: 'kremsi-hero-bowl',
    query: 'Beautiful%20Greek%20yogurt%20bowl%20served%20in%20elegant%20ceramic%20dish%2C%20topped%20with%20fresh%20berries%20strawberries%20blueberries%2C%20crunchy%20granola%2C%20drizzle%20of%20honey%2C%20warm%20golden%20lighting%2C%20minimalist%20white%20marble%20surface%2C%20soft%20shadows%2C%20professional%20food%20photography%2C%20editorial%20style%2C%20premium%20aesthetic%2C%20lifestyle%20photography',
    label: 'Bowl',
  },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center bg-background-50 overflow-hidden pt-[72px]"
    >
      {/* Greek decorative background - columns */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
        <div className="absolute -left-20 top-1/4 opacity-[0.06]">
          <div className="w-40 h-[600px] border border-primary-300 rounded-t-full"></div>
          <div className="w-40 h-[600px] border border-primary-300 rounded-t-full ml-16 -mt-[550px]"></div>
        </div>
        <div className="absolute -right-16 bottom-1/4 opacity-[0.06]">
          <div className="w-32 h-[400px] border border-primary-300 rounded-t-full"></div>
          <div className="w-32 h-[400px] border border-primary-300 rounded-t-full ml-12 -mt-[360px]"></div>
        </div>
        {/* Greek key pattern */}
        <div className="absolute top-1/2 left-0 right-0 opacity-[0.04]">
          <div className="flex justify-center gap-6">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="w-6 h-6 border border-primary-400 rotate-45"></div>
            ))}
          </div>
        </div>
        {/* Olive branch decorative */}
        <div className="absolute top-32 right-1/3 opacity-[0.05]">
          <svg width="120" height="200" viewBox="0 0 120 200" fill="none">
            <path d="M60 0 C40 30 20 60 10 90 C5 110 15 130 30 140 C50 150 70 130 60 110 C55 100 45 105 50 115" stroke="currentColor" strokeWidth="1" className="text-primary-400"/>
            {[30, 50, 70, 90, 110, 130, 150].map((y, i) => (
              <ellipse key={i} cx={60 + (i % 2 === 0 ? 15 : -15)} cy={y} rx="8" ry="4" fill="currentColor" className="text-primary-300" transform={`rotate(${i % 2 === 0 ? 20 : -20} ${60 + (i % 2 === 0 ? 15 : -15)} ${y})`}/>
            ))}
          </svg>
        </div>
      </div>

      <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left content */}
          <div
            ref={ref}
            className={`flex-1 text-center lg:text-left scroll-reveal ${isVisible ? 'visible' : ''}`}
          >
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.15] font-bold text-foreground-900 mb-6 tracking-tight">
              {t('hero.title')}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href="#catalogo"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#catalogo')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 min-w-[180px]"
              >
                {t('hero.cta_catalog')}
              </a>
              <a
                href="#puntos-venta"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#puntos-venta')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 bg-transparent text-foreground-700 border border-foreground-200 text-sm font-semibold rounded-full hover:border-primary-400 hover:text-primary-500 transition-all duration-300 min-w-[180px]"
              >
                {t('hero.cta_locations')}
              </a>
            </div>
          </div>

          {/* Right - Product composition */}
          <div className="flex-1 relative">
            <div className="relative w-full max-w-[560px] mx-auto aspect-[4/3]">
              {/* Main bowl image */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] aspect-square z-20">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20Greek%20yogurt%20bowl%20served%20in%20elegant%20ceramic%20dish%2C%20topped%20with%20fresh%20berries%20strawberries%20blueberries%2C%20crunchy%20granola%2C%20drizzle%20of%20honey%2C%20warm%20golden%20lighting%2C%20minimalist%20white%20marble%20surface%2C%20soft%20shadows%2C%20professional%20food%20photography%2C%20editorial%20style%2C%20premium%20aesthetic%2C%20lifestyle%20photography&width=800&height=800&seq=kremsi-hero-bowl&orientation=squarish"
                  alt="Kremsi Greek Yogurt Bowl"
                  className="w-full h-full object-cover rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
                />
              </div>

              {/* Floating products around */}
              <div className="absolute top-[8%] right-[8%] w-[30%] z-30 animate-float" style={{ animationDelay: '0s' }}>
                <div className="w-full aspect-square bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden flex items-center justify-center p-2">
                  <img
                    src="https://storage.readdy-site.link/project_files/7457165c-176b-4bb4-a41c-5aebca155c44/02453041-4b89-4fa0-acd7-1bd33309430b_compressed_Natural440g.webp"
                    alt="Kremsi Natural 440g"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="absolute top-[5%] left-[5%] w-[28%] z-30 animate-float" style={{ animationDelay: '1s' }}>
                <div className="w-full aspect-square bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden flex items-center justify-center p-2">
                  <img
                    src="https://storage.readdy-site.link/project_files/7457165c-176b-4bb4-a41c-5aebca155c44/35a81b47-1ac6-4c5d-8477-390f4f955b46_compressed_MM130g.webp"
                    alt="Kremsi Mango Maracuyá 130g"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="absolute bottom-[5%] left-[10%] w-[26%] z-30 animate-float" style={{ animationDelay: '2s' }}>
                <div className="w-full aspect-square bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden flex items-center justify-center p-2">
                  <img
                    src="https://storage.readdy-site.link/project_files/7457165c-176b-4bb4-a41c-5aebca155c44/1d447eda-1cc1-4775-8fef-66f3284c365f_compressed_Taro950g.webp"
                    alt="Kremsi Taro 950g"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Decorative ring */}
              <div className="absolute inset-0 rounded-full border border-primary-200/30 z-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
        <span className="text-xs text-foreground-400 tracking-widest uppercase">Scroll</span>
        <div className="w-5 h-8 border border-foreground-300 rounded-full flex items-start justify-center p-1">
          <div className="w-1 h-2 bg-foreground-400 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}